import PaginationButton from '@/components/PaginationButton'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { pageSlots } from '../projectDirectory'

interface ProjectsPaginationProps {
  page: number
  pageCount: number
  onPageChange: (page: number) => void
}

/**
 * Previous / numbered pages / Next for the project grid.
 *
 * Renders nothing below two pages: a pager that can only point at the page you
 * are on is furniture.
 */
export default function ProjectsPagination({
  page,
  pageCount,
  onPageChange,
}: ProjectsPaginationProps) {
  const { t } = useUiStrings()
  if (pageCount < 2) return null

  return (
    <nav aria-label={t.projectsHeading} className="flex flex-wrap items-center gap-1">
      {/* A glyph, not an icon beside the word: the arrows are keys of the same
          pager as the numbers, and History's read that way first. The word
          survives as the accessible name. */}
      <PaginationButton
        label={t.previousPage}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        ‹
      </PaginationButton>

      {pageSlots(page, pageCount).map((slot, index) =>
        slot === 'gap' ? (
          <span
            // Gaps carry no page, so their position is the only stable key.
            key={`gap-${index}`}
            aria-hidden="true"
            className="px-1 text-xs text-[var(--text-tertiary)]"
          >
            …
          </span>
        ) : (
          <PaginationButton
            key={slot}
            label={fmt(t.pageN, { n: slot })}
            active={slot === page}
            onClick={() => onPageChange(slot)}
          >
            {slot}
          </PaginationButton>
        )
      )}

      <PaginationButton
        label={t.nextPage}
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
      >
        ›
      </PaginationButton>
    </nav>
  )
}
